// The alert-cluster card's glyphs.
//
// The 2026-09-14 design puts an icon back beside every finding, in a tinted
// square that carries the finding's tone. That reverses the previous pass,
// which had stripped them: back then two of the four rungs usually held an
// ABSENCE, so six tinted squares competed with the values they sat next to.
// What changed is that absences no longer get a tile at all — they are the
// "Additional checks" rows at the foot of the card — so every square that
// remains sits beside something that was actually found.
//
// House rules, so a glyph added later does not arrive in a different accent:
//   • 24x24 viewBox, 1.7 stroke, round caps and joins, no fill;
//   • `currentColor` only — the caller sets the hue, and a literal here would
//     survive a theme flip as a dark-theme blue on a white card (the exact
//     bug the Netwatch mark was fixed for in marks.tsx);
//   • aria-hidden: every glyph sits beside a text label that already says the
//     same thing, so announcing it would only double the row. The two
//     feedback verdicts are the exception a caller must handle — their
//     buttons carry the word too, so the glyph stays hidden there as well.
//   • the drawn size is 18px, and CSS may override it: a presentational
//     width/height attribute loses to a stylesheet, which is how the tile
//     squares get 20px glyphs without a second component.

const S = {
  width: 18,
  height: 18,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.7,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
} as const

/** Nothing is still open — and, on the checks row, a question that was asked
 *  and came back empty. */
export function AllClearIcon() {
  return (
    <svg {...S}>
      <circle cx="12" cy="12" r="8.8" />
      <path d="M8.4 12.2l2.5 2.5 4.7-5.1" />
    </svg>
  )
}

/** Something is still open. */
export function StillOpenIcon() {
  return (
    <svg {...S}>
      <circle cx="12" cy="12" r="8.8" />
      <path d="M12 7.9v4.4M12 16.1h.01" />
    </svg>
  )
}

/** The host group the cluster's hosts share — a rack, because a group is a
 *  set of machines and not a folder. */
export function GroupIcon() {
  return (
    <svg {...S}>
      <rect x="3.6" y="4.4" width="16.8" height="6.4" rx="2" />
      <rect x="3.6" y="13.2" width="16.8" height="6.4" rx="2" />
      <path d="M7.2 7.6h.01M7.2 16.4h.01" />
    </svg>
  )
}

/** The alert the cluster is mostly made of. */
export function BellIcon() {
  return (
    <svg {...S}>
      <path d="M18 8.6a6 6 0 1 0-12 0c0 5.4-2 7-2 7h16s-2-1.6-2-7Z" />
      <path d="M13.7 19.2a2 2 0 0 1-3.4 0" />
    </svg>
  )
}

/** A Zabbix tag every host in the cluster carries. */
export function TagIcon() {
  return (
    <svg {...S}>
      <path d="M11.3 3.8H4.6a.8.8 0 0 0-.8.8v6.7c0 .2.1.4.2.6l8.4 8.4a1.6 1.6 0 0 0 2.3 0l5.6-5.6a1.6 1.6 0 0 0 0-2.3l-8.4-8.4a.8.8 0 0 0-.6-.2Z" />
      <path d="M8 8h.01" />
    </svg>
  )
}

/** The change recorded before the first alert. A commit mark, not a warning
 *  triangle: this row states a coincidence in time, never a verdict. */
export function CauseIcon() {
  return (
    <svg {...S}>
      <circle cx="12" cy="12" r="3.4" />
      <path d="M3.6 12h5M15.4 12h5" />
    </svg>
  )
}

/** The same cluster, earlier in the last 30 days. */
export function RepeatIcon() {
  return (
    <svg {...S}>
      <path d="M4.2 11.2a7.8 7.8 0 1 1 1.5 5.4" />
      <path d="M3.4 20v-4.2h4.2" />
      <path d="M12 8.2V12l2.6 1.6" />
    </svg>
  )
}

/** Opens a hint. Small by design — it sits beside a heading and must not
 *  compete with it. */
export function InfoIcon() {
  return (
    <svg {...S} width={14} height={14}>
      <circle cx="12" cy="12" r="8.8" />
      <path d="M12 11.2v4.6M12 8.2h.01" />
    </svg>
  )
}

/** The feedback row's three verdicts. */
export function ThumbUpIcon() {
  return (
    <svg {...S} width={15} height={15}>
      <path d="M7.4 20.2V10.4l4-7.2a2 2 0 0 1 2.8 2.4l-1.1 4.3h5a2 2 0 0 1 1.9 2.5l-1.6 6a2 2 0 0 1-1.9 1.5H7.4Z" />
      <path d="M7.4 10.6H4.6a1 1 0 0 0-1 1v7.6a1 1 0 0 0 1 1h2.8" />
    </svg>
  )
}

export function PartlyIcon() {
  return (
    <svg {...S} width={15} height={15}>
      <circle cx="12" cy="12" r="8.8" />
      <path d="M8.2 12h7.6" />
    </svg>
  )
}

export function ThumbDownIcon() {
  return (
    <svg {...S} width={15} height={15}>
      <path d="M16.6 3.8v9.8l-4 7.2a2 2 0 0 1-2.8-2.4l1.1-4.3h-5a2 2 0 0 1-1.9-2.5l1.6-6a2 2 0 0 1 1.9-1.5h9.1Z" />
      <path d="M16.6 13.4h2.8a1 1 0 0 0 1-1V4.8a1 1 0 0 0-1-1h-2.8" />
    </svg>
  )
}

/** Opens the note box. Named "Add context" on the card: the box takes a
 *  sentence for the Netwatch team, not a fourth verdict. */
export function CommentIcon() {
  return (
    <svg {...S} width={15} height={15}>
      <path d="M20.4 12.6a7.6 7.6 0 0 1-8.2 7.6 8.6 8.6 0 0 1-3.2-.7L3.6 21l1.5-5.2a7.6 7.6 0 0 1-.9-3.6 7.6 7.6 0 0 1 7.6-7.6h.5a7.6 7.6 0 0 1 7.1 7.1v.9Z" />
    </svg>
  )
}

/** The scale strip's direction marks. Drawn, not typed: a text arrow takes
 *  the body font's weight and sits on the text baseline, which made the
 *  24-hour rule read as a sentence with two stray glyphs in it. */
export function ScaleArrow({ dir }: { dir: 'left' | 'right' }) {
  return (
    <svg {...S} width={11} height={11}>
      {dir === 'left'
        ? <path d="M20 12H4.8M10.4 6.4 4.8 12l5.6 5.6" />
        : <path d="M4 12h15.2M13.6 6.4 19.2 12l-5.6 5.6" />}
    </svg>
  )
}

/**
 * The Outlook row's mark.
 *
 * Filled, not stroked, and therefore the one exception to this file's house
 * rules — it sits in a 48px tinted square as the page's entry point to the
 * card, where a 1.7px outline at that size reads as a wireframe rather than a
 * mark. Still `currentColor` only.
 *
 * It replaces the Netwatch AtomMark the row wore, which the posture line
 * sharing this slot also wears. The two rows are no longer the same kind of
 * thing: the posture line is written, this one is computed and opens into a
 * card, and the 2026-09-14 design gives it its own glyph to say so.
 */
export function SparkMark() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M13.1 2.4a.6.6 0 0 0-1.15 0l-1.3 4.2a4.4 4.4 0 0 1-2.9 2.9l-4.2 1.3a.6.6 0 0 0 0 1.15l4.2 1.3a4.4 4.4 0 0 1 2.9 2.9l1.3 4.2a.6.6 0 0 0 1.15 0l1.3-4.2a4.4 4.4 0 0 1 2.9-2.9l4.2-1.3a.6.6 0 0 0 0-1.15l-4.2-1.3a4.4 4.4 0 0 1-2.9-2.9Z" />
      <path d="M5.1 1.6a.36.36 0 0 0-.7 0l-.5 1.7a1.9 1.9 0 0 1-1.25 1.25l-1.7.5a.36.36 0 0 0 0 .7l1.7.5A1.9 1.9 0 0 1 3.9 7.5l.5 1.7a.36.36 0 0 0 .7 0l.5-1.7A1.9 1.9 0 0 1 6.85 6.25l1.7-.5a.36.36 0 0 0 0-.7l-1.7-.5A1.9 1.9 0 0 1 5.6 3.3Z" opacity=".72" />
    </svg>
  )
}
